// Browser-owned attribution boundary. URL and storage values are untrusted.
export const ATTRIBUTION_KEY = 'zoe_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const CLICK_KEYS = ['gclid', 'fbclid', 'li_fat_id'] as const;
const CAMPAIGN_KEYS = [...UTM_KEYS, ...CLICK_KEYS] as const;
const HANDOFF_KEYS = [
  ...CAMPAIGN_KEYS.flatMap((k) => [`first_${k}`, `last_${k}`]),
  'first_landing_page', 'last_landing_page', 'first_referrer', 'last_referrer',
  'first_touched_at', 'last_touched_at',
] as const;
const ALLOWED_KEYS = new Set<string>([...UTM_KEYS, ...CLICK_KEYS, 'referrer', 'landing_path', ...HANDOFF_KEYS]);
const MAX_VALUE = 256;
const MAX_URL = 1024;
// Route families whose second segment is a credential, a codename, a company slug or a record id.
// `join` is a token route too: token-guard.js normally swaps it for `~`, but leaves it in the URL when
// it cannot park it (blocked storage). `portco/:slug` names a portfolio company.
const PRIVATE_ROUTE: Record<string, string> = { invite: ':token', share: ':token', survey: ':token', 'founder-followup': ':token', join: ':token', deal: ':codename', portco: ':slug', sources: ':id' };

export interface Attribution { [key: string]: string | undefined; utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_term?: string; utm_content?: string; referrer?: string; landing_path?: string; }

const OPTOUT_KEYS = ['zoe.analytics.opt_out', 'zoe_analytics_opt_out', 'analytics_opt_out', 'posthog_opt_out'] as const;
export function attributionAllowed(): boolean {
  if (typeof navigator !== 'undefined') {
    const dnt = navigator.doNotTrack;
    const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
    if (dnt === '1' || dnt?.toLowerCase() === 'yes' || gpc === true) return false;
  }
  try {
    return !OPTOUT_KEYS.some((key) => ['1', 'true', 'yes'].includes((window.localStorage.getItem(key) ?? '').toLowerCase()));
  } catch { return true; }
}

function decoded(value: string): string | undefined {
  try {
    let result = value;
    for (let i = 0; i < 3; i++) {
      const next = decodeURIComponent(result);
      if (next === result) return result;
      result = next;
    }
    return /%[0-9a-f]{2}/i.test(result) ? undefined : result;
  } catch { return undefined; }
}
function privateCharacters(value: string): boolean {
  return value.includes('@') || [...value].some((char) => { const code = char.charCodeAt(0); return code < 32 || code === 127; });
}
function safeValue(value: string | null | undefined, max = MAX_VALUE): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  const d = decoded(v);
  if (!v || v.length > max || d === undefined || privateCharacters(d) || /https?:\/\//i.test(d) || /[?#]/.test(d)) return undefined;
  if (/\b(?:bearer|secret|password|session|token|challenge)\b/i.test(d)) return undefined;
  return v;
}
function cleanUrl(value: string | null | undefined, max = MAX_URL): string | undefined {
  if (!value || value.length > max) return undefined;
  try {
    const url = new URL(value, 'https://zoe.invalid');
    if (!/^https?:$/.test(url.protocol) && url.origin !== 'https://zoe.invalid') return undefined;
    if (url.username || url.password) return undefined;
    const decodedPath = decoded(url.pathname);
    if (decodedPath === undefined || privateCharacters(decodedPath) || /[?#]/.test(decodedPath)) return undefined;
    const parts = decodedPath.split('/');
    const family = parts[1];
    if (PRIVATE_ROUTE[family] && parts.length > 2 && parts[2]) {
      // Keep known static route shape, never additional private identifiers.
      const report = family === 'deal' && parts[3] === 'report';
      parts.splice(2, parts.length - 2, PRIVATE_ROUTE[family], ...(report ? ['report'] : []));
    }
    const path = parts.join('/');
    if (!PRIVATE_ROUTE[family] && (/\b(?:bearer|secret|password|session|token|challenge)\b/i.test(path) || /[A-Za-z0-9_-]{32,}\.[A-Za-z0-9_-]{8,}/.test(path))) return undefined;
    if (url.origin === 'https://zoe.invalid') return path;
    return `${url.origin}${path}`;
  } catch { return undefined; }
}
function cleanReferrer(value: string | null | undefined): string | undefined {
  const result = cleanUrl(value);
  return result && /^https?:\/\//.test(result) ? result : undefined;
}
function validTime(value: string | undefined): string | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)) return undefined;
  const t = Date.parse(value); if (!Number.isFinite(t) || t > Date.now() + 300_000) return undefined;
  const date = new Date(t); const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?Z$/);
  if (!match || date.getUTCFullYear() !== +match[1] || date.getUTCMonth() + 1 !== +match[2] || date.getUTCDate() !== +match[3] || date.getUTCHours() !== +match[4] || date.getUTCMinutes() !== +match[5] || date.getUTCSeconds() !== +match[6]) return undefined;
  return date.toISOString();
}
function campaign(record: Record<string, string | undefined>, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) { const value = record[`${prefix}${key}`]; if (value) out[key] = value; }
  return out;
}
function hasCampaign(record: Record<string, string | undefined>): boolean { return CAMPAIGN_KEYS.some((k) => Boolean(record[k])); }
function equalCampaign(a: Record<string, string>, b: Record<string, string>): boolean { return CAMPAIGN_KEYS.every((k) => a[k] === b[k]); }
function queryValues(search: string): Record<string, string> {
  const params = new URLSearchParams(search); const out: Record<string, string> = {};
  for (const key of [...CAMPAIGN_KEYS, ...HANDOFF_KEYS]) {
    const raw = params.get(key); const value = key.endsWith('_touched_at') ? validTime(raw ?? undefined) : key.includes('referrer') ? cleanReferrer(raw) : key.includes('landing_page') ? cleanUrl(raw) : safeValue(raw);
    if (value) out[key] = value;
  }
  return out;
}
function currentObservation(search: string, referrer: string, path: string): Attribution {
  const q = queryValues(search); const out: Attribution = {};
  for (const key of CAMPAIGN_KEYS) if (q[key]) out[key] = q[key];
  const landing = cleanUrl(path); const ref = cleanReferrer(referrer);
  if (landing) out.landing_path = landing;
  if (ref) out.referrer = ref;
  return out;
}
function flatten(first: Attribution, last: Attribution | undefined): Attribution {
  const out: Attribution = {};
  for (const key of CAMPAIGN_KEYS) { if (first[`first_${key}`]) { out[`first_${key}`] = first[`first_${key}`]; out[key] = first[`first_${key}`]; } if (last?.[`last_${key}`]) out[`last_${key}`] = last[`last_${key}`]; }
  for (const key of ['landing_page', 'referrer'] as const) { if (first[`first_${key}`]) out[`first_${key}`] = first[`first_${key}`]; if (last?.[`last_${key}`]) out[`last_${key}`] = last[`last_${key}`]; }
  if (first.first_referrer) out.referrer = first.first_referrer;
  if (first.first_landing_page) out.landing_path = first.first_landing_page;
  if (first.first_touched_at) out.first_touched_at = first.first_touched_at;
  if (last?.last_touched_at) out.last_touched_at = last.last_touched_at;
  return out;
}
function snapshot(first: boolean, source: Attribution, query: Record<string, string>, now: string): Attribution {
  const prefix = first ? 'first_' : 'last_'; const out: Attribution = {};
  const explicit = campaign(query, prefix); const regular = campaign(source); const hasExplicit = HANDOFF_KEYS.some((key) => key.startsWith(prefix) && query[key]); const values = hasExplicit ? explicit : regular;
  for (const key of CAMPAIGN_KEYS) if (values[key]) out[`${prefix}${key}`] = values[key];
  const page = query[`${prefix}landing_page`] ?? source.landing_path;
  const ref = query[`${prefix}referrer`] ?? source.referrer;
  if (page) out[`${prefix}landing_page`] = page;
  if (ref) out[`${prefix}referrer`] = ref;
  out[`${prefix}touched_at`] = query[`${prefix}touched_at`] ?? now;
  return out;
}

export function captureAttribution(search: string, referrer: string, path: string): Attribution {
  const query = queryValues(search); const source = currentObservation(search, referrer, path); const now = new Date().toISOString();
  const first = snapshot(true, source, query, now);
  const last = hasCampaign(campaign(source)) || Object.keys(campaign(query, 'last_')).length ? snapshot(false, source, query, now) : undefined;
  return flatten(first, last);
}

function restore(raw: string | null): Attribution | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw); if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const out: Attribution = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!ALLOWED_KEYS.has(key) || typeof value !== 'string') continue;
      const clean = key.endsWith('_touched_at') ? validTime(value) : key.includes('referrer') ? cleanReferrer(value) : key.includes('landing') || key === 'landing_path' ? cleanUrl(value) : safeValue(value);
      if (clean) out[key] = clean;
    }
    const explicitFirst = HANDOFF_KEYS.some((key) => key.startsWith('first_') && out[key]);
    if (!explicitFirst) {
      for (const key of CAMPAIGN_KEYS) if (out[key] && !out[`first_${key}`]) out[`first_${key}`] = out[key];
      if (out.referrer && !out.first_referrer) out.first_referrer = out.referrer;
      if (out.landing_path && !out.first_landing_page) out.first_landing_page = out.landing_path;
    }
    return Object.keys(out).length ? out : null;
  } catch { return null; }
}

let cached: Attribution | null = null;
export function getAttribution(): Attribution {
  if (!attributionAllowed()) return {};
  let stored: Attribution | null = null; try { stored = restore(localStorage.getItem(ATTRIBUTION_KEY)); } catch { /* unavailable */ }
  const source = typeof window === 'undefined' ? {} : currentObservation(window.location.search, document.referrer, window.location.pathname);
  const query = typeof window === 'undefined' ? {} : queryValues(window.location.search); const now = new Date().toISOString();
  const base = stored ?? cached ?? {};
  const first = Object.keys(campaign(base, 'first_')).length || base.first_landing_page || base.first_referrer ? base : snapshot(true, source, query, now);
  const previousLast = campaign(base, 'last_'); const candidate = hasCampaign(campaign(source)) || Object.keys(campaign(query, 'last_')).length ? snapshot(false, source, query, now) : undefined;
  let last = previousLast ? { ...base } : undefined;
  if (candidate) {
    const candidateCampaign = campaign(candidate, 'last_'); const oldTime = validTime(base.last_touched_at); const candidateTime = validTime(candidate.last_touched_at);
    const nonNewerExplicit = Boolean(query.last_touched_at && oldTime && candidateTime && Date.parse(candidateTime) <= Date.parse(oldTime));
    if (!nonNewerExplicit && (!last || !equalCampaign(previousLast, candidateCampaign))) last = candidate;
    else if (last && equalCampaign(previousLast, candidateCampaign)) last.last_touched_at = base.last_touched_at;
  }
  const result = flatten(first, last); cached = result;
  try { localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(result)); } catch { /* in-memory only */ }
  return result;
}
export function _resetAttribution(): void { cached = null; }
